export type FeatureCategory = '学习' | '工具' | '游戏' | '设置';

export type StudentMainTabName =
  | 'index'
  | 'ai-assistant'
  | 'study-plan'
  | 'learning-analysis'
  | 'profile';

export type StudentMainTabItem = {
  name: StudentMainTabName;
  title: string;
  href: string;
};

export const STUDENT_MAIN_TABS: StudentMainTabItem[] = [
  { name: 'index', title: '学习', href: '/' },
  { name: 'ai-assistant', title: '问答', href: '/ai-assistant' },
  { name: 'study-plan', title: '计划', href: '/study-plan' },
  { name: 'learning-analysis', title: '轨迹', href: '/learning-analysis' },
  { name: 'profile', title: '我的', href: '/profile' },
];

export const PHOTO_SEARCH_PREVIEW_ROUTE = '/photo-search';
export const DIAGRAM_QUESTION_ROUTE = '/diagram-question';

export const coreStateCoverageRoutes = [
  PHOTO_SEARCH_PREVIEW_ROUTE,
  DIAGRAM_QUESTION_ROUTE,
  '/wrong-question-practice',
  '/paper-analysis',
  '/knowledge-graph',
] as const;

export type FeatureRouteItem = {
  id: string;
  title: string;
  description: string;
  href: string;
  category: FeatureCategory;
};

export const featureRoutes: FeatureRouteItem[] = [
  {
    id: 'all-functions',
    title: '全部功能',
    description: '查看所有学习、工具、游戏和设置入口',
    href: '/all-functions',
    category: '设置',
  },
  {
    id: 'photo-search',
    title: '拍照搜题',
    description: '上传题图，进入 AI 流式讲解',
    href: '/photo-search',
    category: '学习',
  },
  {
    id: 'diagram-question',
    title: '函数与几何图像题',
    description: '查看原图、确认可信状态并探索受约束动点',
    href: '/diagram-question',
    category: '学习',
  },
  {
    id: 'homework-correction',
    title: '作业批改',
    description: '检查作业结果并给出订正建议',
    href: '/homework-correction',
    category: '学习',
  },
  {
    id: 'weakness-improvement',
    title: '薄弱提升',
    description: '围绕薄弱知识点进行专项训练',
    href: '/weakness-improvement',
    category: '学习',
  },
  {
    id: 'smart-practice',
    title: '智能练习',
    description: '根据当前掌握度推荐练习内容',
    href: '/smart-practice',
    category: '学习',
  },
  {
    id: 'growth-trajectory',
    title: '成长轨迹',
    description: '查看阶段性学习变化和考试节点',
    href: '/growth-trajectory',
    category: '学习',
  },
  {
    id: 'analysis-result',
    title: '分析报告',
    description: '查看单次测评或练习分析结果',
    href: '/analysis-result',
    category: '学习',
  },
  {
    id: 'paper-analysis',
    title: '试卷分析',
    description: '查看试卷成绩、错题分布和薄弱知识点占位页',
    href: '/paper-analysis',
    category: '学习',
  },
  {
    id: 'conversation-history',
    title: '对话历史',
    description: '查看 AI 学伴历史会话和搜索入口',
    href: '/conversation-history',
    category: '学习',
  },
  {
    id: 'wrong-question-practice',
    title: '错题再练',
    description: '承接错题本详情中的再练一次入口',
    href: '/wrong-question-practice',
    category: '学习',
  },
  {
    id: 'subject-analysis',
    title: '学科分析',
    description: '深入查看单学科知识点表现',
    href: '/subject-analysis',
    category: '学习',
  },
  {
    id: 'notes',
    title: '学习笔记',
    description: '整理错题、知识点和复盘记录',
    href: '/notes',
    category: '工具',
  },
  {
    id: 'calculator',
    title: '计算器',
    description: '快速完成学习中的基础计算',
    href: '/calculator',
    category: '工具',
  },
  {
    id: 'word-lookup',
    title: '单词查询',
    description: '查询英语单词释义和例句',
    href: '/word-lookup',
    category: '工具',
  },
  {
    id: 'ancient-text-analysis',
    title: '古文解析',
    description: '辅助理解文言文重点词句',
    href: '/ancient-text-analysis',
    category: '工具',
  },
  {
    id: 'question-collection',
    title: '题目收藏',
    description: '保存需要反复查看的题目',
    href: '/question-collection',
    category: '工具',
  },
  {
    id: 'mock-exam',
    title: '模拟考试',
    description: '按考试节奏完成整套练习',
    href: '/mock-exam',
    category: '工具',
  },
  {
    id: 'oral-calculation',
    title: '口算 PK',
    description: '通过限时计算训练反应速度',
    href: '/games/oral-calculation',
    category: '游戏',
  },
  {
    id: 'idiom-battle',
    title: '成语 PK',
    description: '用对战练习成语理解和运用',
    href: '/games/idiom-battle',
    category: '游戏',
  },
  {
    id: 'poetry-contest',
    title: '诗词大赛',
    description: '通过题目挑战诗词积累',
    href: '/games/poetry-contest',
    category: '游戏',
  },
  {
    id: 'theme-settings',
    title: '主题设置',
    description: '管理界面主题和显示偏好',
    href: '/theme-settings',
    category: '设置',
  },
  {
    id: 'settings',
    title: '设置',
    description: '账号、偏好、协议和关于入口',
    href: '/settings',
    category: '设置',
  },
  {
    id: 'login',
    title: '登录',
    description: '手机号验证码登录流程骨架',
    href: '/login',
    category: '设置',
  },
];

export const findFeatureRouteById = (id: string): FeatureRouteItem | null =>
  featureRoutes.find((route) => route.id === id) ?? null;
